import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from './context';
import { toast } from 'sonner';
import DashboardShell from './DashboardShell';
import { Zap, RefreshCw, Check, Settings } from './components/DeskeyIcons';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Button } from './components/ui/button';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Switch } from './components/ui/switch';
import { Skeleton } from './components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TASK_TYPES = [
  { id: 'lesson_draft_generation', label: 'Lesson Draft Generation' },
  { id: 'quiz_generation', label: 'Quiz Generation' },
  { id: 'explanation_rewrite', label: 'Explanation Rewrite' },
  { id: 'syllabus_planning', label: 'Syllabus Planning' },
  { id: 'transcription', label: 'Transcription' },
];

const PROVIDERS = ['AUTO', 'openai', 'gemini', 'claude'];
const QUALITY_MODES = ['balanced', 'max_quality', 'faster'];

export default function AdminAISettings() {
  const { user, token } = useApp();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/tenants/${user.tenant_id}/ai-settings`, { headers })
      .then(r => setSettings(r.data))
      .catch(() => toast.error('Failed to load AI settings'))
      .finally(() => setLoading(false));
  }, [user?.tenant_id]);

  const save = async () => {
    setSaving(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.put(`${API}/tenants/${user.tenant_id}/ai-settings`, settings, { headers });
      setSettings(res.data);
      toast.success('AI settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <DashboardShell title="AI Settings"><Skeleton className="h-96 rounded-2xl" /></DashboardShell>;

  return (
    <DashboardShell title="AI Settings" breadcrumb="Admin Dashboard">
      <div className="space-y-6 fade-in max-w-2xl">
        {/* Provider Mode */}
        <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="w-4 h-4" style={{ color: 'hsl(var(--primary))' }} />
              Default AI Provider
            </CardTitle>
            <CardDescription>
              AUTO mode is recommended. Desky routes each task to the best available provider automatically.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm mb-2 block">Default Provider</Label>
              <Select value={settings?.default_provider || 'AUTO'}
                onValueChange={v => setSettings(s => ({ ...s, default_provider: v }))}>
                <SelectTrigger data-testid="ai-provider-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDERS.map(p => (
                    <SelectItem key={p} value={p}>
                      {p === 'AUTO' ? '⚡ AUTO (Recommended)' : p.charAt(0).toUpperCase() + p.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {settings?.default_provider === 'AUTO' && (
                <p className="text-xs mt-2 p-3 rounded-lg" style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--muted-foreground))' }}>
                  AUTO mode intelligently routes tasks: lesson generation → GPT-4o, quick explanations → fast model, etc.
                </p>
              )}
            </div>

            <div>
              <Label className="text-sm mb-2 block">Quality Mode</Label>
              <Select value={settings?.quality_mode || 'balanced'}
                onValueChange={v => setSettings(s => ({ ...s, quality_mode: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUALITY_MODES.map(m => (
                    <SelectItem key={m} value={m}>{m.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm mb-2 block">Fallback Provider</Label>
              <Select value={settings?.fallback_provider || 'openai'}
                onValueChange={v => setSettings(s => ({ ...s, fallback_provider: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['openai', 'gemini', 'claude'].map(p => (
                    <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Task Routing */}
        <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
          <CardHeader>
            <CardTitle className="text-base">Per-Task Provider Override</CardTitle>
            <CardDescription>Override the default provider for specific AI task types.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {TASK_TYPES.map(task => {
                const taskConfig = settings?.task_configs?.find(tc => tc.task_type === task.id);
                const provider = taskConfig?.provider || 'AUTO';
                return (
                  <div key={task.id} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: 'hsl(var(--border))' }}>
                    <Label className="text-sm font-medium">{task.label}</Label>
                    <Select value={provider} onValueChange={v => {
                      const updated = (settings?.task_configs || []).filter(tc => tc.task_type !== task.id);
                      if (v !== 'AUTO') updated.push({ task_type: task.id, provider: v, enabled: true });
                      setSettings(s => ({ ...s, task_configs: updated }));
                    }}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROVIDERS.map(p => <SelectItem key={p} value={p}>{p === 'AUTO' ? 'AUTO' : p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Button onClick={save} disabled={saving} data-testid="save-ai-settings-button"
          style={{ background: 'hsl(var(--primary))', color: 'white', borderRadius: '0.75rem' }}>
          {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save AI Settings'}
        </Button>
      </div>
    </DashboardShell>
  );
}
